import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  FolderOpen,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Star,
  Tag,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { knowledgeBaseApi } from "@/services/api";

// ============================================================================
// CATEGORY FORM
// ============================================================================
const CategoryForm = ({ category, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    name: category?.name || "",
    description: category?.description || "",
    icon: category?.icon || "FileText",
    color: category?.color || "#6366f1",
    is_active: category?.is_active ?? true,
  });

  const iconOptions = [
    "FileText",
    "Book",
    "HelpCircle",
    "Rocket",
    "Shield",
    "Plug",
    "CreditCard",
    "Users",
    "Settings",
    "Code",
    "Zap",
    "Lock",
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Getting Started"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder="Learn the basics of using eScribe"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="icon">Icon</Label>
          <Select
            value={formData.icon}
            onValueChange={(value) => setFormData({ ...formData, icon: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {iconOptions.map((icon) => (
                <SelectItem key={icon} value={icon}>
                  {icon}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="color">Color</Label>
          <div className="flex items-center gap-2">
            <Input
              type="color"
              id="color"
              value={formData.color}
              onChange={(e) =>
                setFormData({ ...formData, color: e.target.value })
              }
              className="w-12 h-10 p-1 cursor-pointer"
            />
            <Input
              value={formData.color}
              onChange={(e) =>
                setFormData({ ...formData, color: e.target.value })
              }
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="is_active">Active</Label>
        <Switch
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, is_active: checked })
          }
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {category ? "Update" : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
};

// ============================================================================
// ARTICLE FORM
// ============================================================================
const ArticleForm = ({
  article,
  categories,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    title: article?.title || "",
    excerpt: article?.excerpt || "",
    content: article?.content || "",
    category_id: article?.category_id || "",
    meta_title: article?.meta_title || "",
    meta_description: article?.meta_description || "",
    is_published: article?.is_published ?? false,
    is_featured: article?.is_featured ?? false,
    visibility: article?.visibility || "public",
    tags: article?.tags?.map((t) => t.id) || [],
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
    >
      <div className="space-y-2">
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="How to send your first document"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category</Label>
        <Select
          value={formData.category_id}
          onValueChange={(value) =>
            setFormData({ ...formData, category_id: value })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {categories?.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="excerpt">Excerpt</Label>
        <Textarea
          id="excerpt"
          value={formData.excerpt}
          onChange={(e) =>
            setFormData({ ...formData, excerpt: e.target.value })
          }
          placeholder="Brief description shown in article listings"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Content (Markdown) *</Label>
        <Textarea
          id="content"
          value={formData.content}
          onChange={(e) =>
            setFormData({ ...formData, content: e.target.value })
          }
          placeholder="Write your article content in Markdown..."
          rows={12}
          className="font-mono text-sm"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="visibility">Visibility</Label>
          <Select
            value={formData.visibility}
            onValueChange={(value) =>
              setFormData({ ...formData, visibility: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="authenticated">Authenticated Users</SelectItem>
              <SelectItem value="admin">Admin Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="is_published">Published</Label>
            <Switch
              id="is_published"
              checked={formData.is_published}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_published: checked })
              }
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="is_featured">Featured</Label>
            <Switch
              id="is_featured"
              checked={formData.is_featured}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_featured: checked })
              }
            />
          </div>
        </div>
      </div>

      <details className="border rounded-lg p-3">
        <summary className="cursor-pointer text-sm font-medium">
          SEO Settings
        </summary>
        <div className="mt-3 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="meta_title">Meta Title</Label>
            <Input
              id="meta_title"
              value={formData.meta_title}
              onChange={(e) =>
                setFormData({ ...formData, meta_title: e.target.value })
              }
              placeholder="Custom title for search engines"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta_description">Meta Description</Label>
            <Textarea
              id="meta_description"
              value={formData.meta_description}
              onChange={(e) =>
                setFormData({ ...formData, meta_description: e.target.value })
              }
              placeholder="Custom description for search engines"
              rows={2}
            />
          </div>
        </div>
      </details>

      <DialogFooter className="sticky bottom-0 bg-background pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {article ? "Update" : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
};

// ============================================================================
// TAG FORM
// ============================================================================
const TagForm = ({ tag, onSubmit, onCancel, isLoading }) => {
  const [name, setName] = useState(tag?.name || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="tagName">Tag Name *</Label>
        <Input
          id="tagName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="getting-started"
          required
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {tag ? "Update" : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export const KnowledgeBase = () => {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("articles");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialogs
  const [categoryDialog, setCategoryDialog] = useState({
    open: false,
    category: null,
  });
  const [articleDialog, setArticleDialog] = useState({
    open: false,
    article: null,
  });
  const [tagDialog, setTagDialog] = useState({ open: false, tag: null });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    item: null,
  });

  // Queries
  const { data: categoriesResponse, isLoading: categoriesLoading } = useQuery({
    queryKey: ["kb-admin-categories"],
    queryFn: async () => {
      const response = await knowledgeBaseApi.admin.getCategories();
      return response.data;
    },
  });

  const { data: articlesResponse, isLoading: articlesLoading } = useQuery({
    queryKey: ["kb-admin-articles", selectedCategory, statusFilter],
    queryFn: async () => {
      const response = await knowledgeBaseApi.admin.getArticles({
        category: selectedCategory !== "all" ? selectedCategory : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      // response.data = { success, data: { articles, total, limit, offset } }
      return response.data?.data || response.data || { articles: [] };
    },
  });

  const { data: tagsResponse, isLoading: tagsLoading } = useQuery({
    queryKey: ["kb-admin-tags"],
    queryFn: async () => {
      const response = await knowledgeBaseApi.admin.getTags();
      return response.data;
    },
  });

  // Extract data arrays with defensive checks
  const categories = Array.isArray(categoriesResponse?.data)
    ? categoriesResponse.data
    : [];
  const articles = Array.isArray(articlesResponse?.articles)
    ? articlesResponse.articles
    : [];
  const tags = Array.isArray(tagsResponse?.data) ? tagsResponse.data : [];

  // Mutations
  const createCategoryMutation = useMutation({
    mutationFn: (data) => knowledgeBaseApi.admin.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-categories"]);
      setCategoryDialog({ open: false, category: null });
      toast.success("Category created");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create category");
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }) =>
      knowledgeBaseApi.admin.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-categories"]);
      setCategoryDialog({ open: false, category: null });
      toast.success("Category updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update category");
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id) => knowledgeBaseApi.admin.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-categories"]);
      setDeleteDialog({ open: false, type: null, item: null });
      toast.success("Category deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete category");
    },
  });

  const createArticleMutation = useMutation({
    mutationFn: (data) => knowledgeBaseApi.admin.createArticle(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-articles"]);
      setArticleDialog({ open: false, article: null });
      toast.success("Article created");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create article");
    },
  });

  const updateArticleMutation = useMutation({
    mutationFn: ({ id, data }) =>
      knowledgeBaseApi.admin.updateArticle(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-articles"]);
      setArticleDialog({ open: false, article: null });
      toast.success("Article updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update article");
    },
  });

  const deleteArticleMutation = useMutation({
    mutationFn: (id) => knowledgeBaseApi.admin.deleteArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-articles"]);
      setDeleteDialog({ open: false, type: null, item: null });
      toast.success("Article deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete article");
    },
  });

  const publishArticleMutation = useMutation({
    mutationFn: (id) => knowledgeBaseApi.admin.publishArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-articles"]);
      toast.success("Article published");
    },
  });

  const unpublishArticleMutation = useMutation({
    mutationFn: (id) => knowledgeBaseApi.admin.unpublishArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-articles"]);
      toast.success("Article unpublished");
    },
  });

  const createTagMutation = useMutation({
    mutationFn: (data) => knowledgeBaseApi.admin.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-tags"]);
      setTagDialog({ open: false, tag: null });
      toast.success("Tag created");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create tag");
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: ({ id, data }) => knowledgeBaseApi.admin.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-tags"]);
      setTagDialog({ open: false, tag: null });
      toast.success("Tag updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update tag");
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: (id) => knowledgeBaseApi.admin.deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["kb-admin-tags"]);
      setDeleteDialog({ open: false, type: null, item: null });
      toast.success("Tag deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete tag");
    },
  });

  // Handlers
  const handleCategorySubmit = (data) => {
    if (categoryDialog.category) {
      updateCategoryMutation.mutate({ id: categoryDialog.category.id, data });
    } else {
      createCategoryMutation.mutate(data);
    }
  };

  const handleArticleSubmit = (data) => {
    if (articleDialog.article) {
      updateArticleMutation.mutate({ id: articleDialog.article.id, data });
    } else {
      createArticleMutation.mutate(data);
    }
  };

  const handleTagSubmit = (data) => {
    if (tagDialog.tag) {
      updateTagMutation.mutate({ id: tagDialog.tag.id, data });
    } else {
      createTagMutation.mutate(data);
    }
  };

  const handleDelete = () => {
    const { type, item } = deleteDialog;
    if (type === "category") {
      deleteCategoryMutation.mutate(item.id);
    } else if (type === "article") {
      deleteArticleMutation.mutate(item.id);
    } else if (type === "tag") {
      deleteTagMutation.mutate(item.id);
    }
  };

  // Filter articles by search
  const filteredArticles = articles.filter((article) =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const renderArticles = () => {
    if (articlesLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </TableCell>
        </TableRow>
      );
    }
    if (filteredArticles?.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={6}
            className="text-center py-8 text-muted-foreground"
          >
            No articles found
          </TableCell>
        </TableRow>
      );
    }
    return filteredArticles.map((article) => (
      <TableRow key={article.id}>
        <TableCell>
          <div className="flex items-center gap-2">
            {article.is_featured && (
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            )}
            <span className="font-medium">{article.title}</span>
          </div>
        </TableCell>
        <TableCell>
          <span className="text-muted-foreground">
            {article.category_name || "Uncategorized"}
          </span>
        </TableCell>
        <TableCell>
          <Badge variant={article.is_published ? "default" : "secondary"}>
            {article.is_published ? "Published" : "Draft"}
          </Badge>
        </TableCell>
        <TableCell>{article.view_count}</TableCell>
        <TableCell className="text-muted-foreground">
          {new Date(article.updated_at).toLocaleDateString()}
        </TableCell>
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setArticleDialog({ open: true, article })}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              {article.is_published ? (
                <DropdownMenuItem
                  onClick={() => unpublishArticleMutation.mutate(article.id)}
                >
                  <EyeOff className="w-4 h-4 mr-2" />
                  Unpublish
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => publishArticleMutation.mutate(article.id)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Publish
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() =>
                  setDeleteDialog({
                    open: true,
                    type: "article",
                    item: article,
                  })
                }
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ));
  };

  const renderCategories = () => {
    if (categoriesLoading) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </TableCell>
        </TableRow>
      );
    }
    if (categories?.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={5}
            className="text-center py-8 text-muted-foreground"
          >
            No categories found
          </TableCell>
        </TableRow>
      );
    }
    return categories.map((category) => (
      <TableRow key={category.id}>
        <TableCell>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: category.color }}
            />
            <span className="font-medium">{category.name}</span>
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground max-w-xs truncate">
          {category.description}
        </TableCell>
        <TableCell>
          {category.published_articles}/{category.total_articles}
        </TableCell>
        <TableCell>
          <Badge variant={category.is_active ? "default" : "secondary"}>
            {category.is_active ? "Active" : "Inactive"}
          </Badge>
        </TableCell>
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setCategoryDialog({ open: true, category })}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() =>
                  setDeleteDialog({
                    open: true,
                    type: "category",
                    item: category,
                  })
                }
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ));
  };

  const renderTags = () => {
    if (tagsLoading) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </TableCell>
        </TableRow>
      );
    }
    if (tags?.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={4}
            className="text-center py-8 text-muted-foreground"
          >
            No tags found
          </TableCell>
        </TableRow>
      );
    }
    return tags.map((tag) => (
      <TableRow key={tag.id}>
        <TableCell className="font-medium">{tag.name}</TableCell>
        <TableCell className="text-muted-foreground">{tag.slug}</TableCell>
        <TableCell>{tag.article_count}</TableCell>
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setTagDialog({ open: true, tag })}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() =>
                  setDeleteDialog({
                    open: true,
                    type: "tag",
                    item: tag,
                  })
                }
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Knowledge Base
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage help articles, categories, and tags
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/knowledge-base">
            <ExternalLink className="w-4 h-4 mr-2" />
            View Public KB
          </Link>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{categories.length}</div>
            <p className="text-sm text-muted-foreground">Categories</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{articles.length}</div>
            <p className="text-sm text-muted-foreground">Total Articles</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">
              {articles.filter((a) => a.is_published).length}
            </div>
            <p className="text-sm text-muted-foreground">Published</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{tags.length}</div>
            <p className="text-sm text-muted-foreground">Tags</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="articles" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Articles
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="tags" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Tags
          </TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles" className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => setArticleDialog({ open: true, article: null })}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Article
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>{renderArticles()}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex items-center justify-end">
            <Button
              onClick={() => setCategoryDialog({ open: true, category: null })}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Category
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Articles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>{renderCategories()}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags" className="space-y-4">
          <div className="flex items-center justify-end">
            <Button onClick={() => setTagDialog({ open: true, tag: null })}>
              <Plus className="w-4 h-4 mr-2" />
              New Tag
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Articles</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>{renderTags()}</TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog
        open={categoryDialog.open}
        onOpenChange={(open) =>
          !open && setCategoryDialog({ open: false, category: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {categoryDialog.category ? "Edit Category" : "New Category"}
            </DialogTitle>
            <DialogDescription>
              Categories help organize your knowledge base articles.
            </DialogDescription>
          </DialogHeader>
          <CategoryForm
            category={categoryDialog.category}
            onSubmit={handleCategorySubmit}
            onCancel={() => setCategoryDialog({ open: false, category: null })}
            isLoading={
              createCategoryMutation.isPending ||
              updateCategoryMutation.isPending
            }
          />
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog
        open={articleDialog.open}
        onOpenChange={(open) =>
          !open && setArticleDialog({ open: false, article: null })
        }
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {articleDialog.article ? "Edit Article" : "New Article"}
            </DialogTitle>
            <DialogDescription>
              Write your article content using Markdown formatting.
            </DialogDescription>
          </DialogHeader>
          <ArticleForm
            article={articleDialog.article}
            categories={categories}
            tags={tags}
            onSubmit={handleArticleSubmit}
            onCancel={() => setArticleDialog({ open: false, article: null })}
            isLoading={
              createArticleMutation.isPending || updateArticleMutation.isPending
            }
          />
        </DialogContent>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog
        open={tagDialog.open}
        onOpenChange={(open) =>
          !open && setTagDialog({ open: false, tag: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tagDialog.tag ? "Edit Tag" : "New Tag"}</DialogTitle>
            <DialogDescription>
              Tags help users find related articles.
            </DialogDescription>
          </DialogHeader>
          <TagForm
            tag={tagDialog.tag}
            onSubmit={handleTagSubmit}
            onCancel={() => setTagDialog({ open: false, tag: null })}
            isLoading={
              createTagMutation.isPending || updateTagMutation.isPending
            }
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          !open && setDeleteDialog({ open: false, type: null, item: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteDialog.type}?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "
              {deleteDialog.item?.name || deleteDialog.item?.title}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setDeleteDialog({ open: false, type: null, item: null })
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={
                deleteCategoryMutation.isPending ||
                deleteArticleMutation.isPending ||
                deleteTagMutation.isPending
              }
            >
              {(deleteCategoryMutation.isPending ||
                deleteArticleMutation.isPending ||
                deleteTagMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBase;
